<template>
  <NuxtLink :to="`/bundles/${bundle.slug}`" class="bundle-card cy-box">
    <div class="bundle-card__art">
      <img v-if="bundle.image" :src="merchImageSrc(bundle.image)" :alt="bundle.title" loading="lazy" />
      <div v-else class="bundle-card__collage" :class="`n${collage.length}`">
        <img v-for="c in collage" :key="c.kind + c.id" :src="merchImageSrc(c.image)" :alt="c.title" loading="lazy" />
      </div>
      <span v-if="bundle.pricing?.savingsPercent > 0" class="bundle-card__badge">−{{ bundle.pricing.savingsPercent }}%</span>
    </div>
    <div class="bundle-card__body">
      <p class="bundle-card__kicker">{{ $t('shop_bundle.kicker') }} · {{ $t('shop_bundle.n_items', bundle.components.length) }}</p>
      <p class="bundle-card__title">{{ bundle.title }}</p>
      <p class="bundle-card__items">{{ bundle.components.map(c => c.title).join(' + ') }}</p>
      <ShopSalePrice
        class="mt-1"
        size="sm"
        :price="bundle.priceCents / 100"
        :list-price="bundle.pricing?.savingsCents > 0 ? bundle.pricing.standaloneSumCents / 100 : null"
        :percent="null"
      />
    </div>
  </NuxtLink>
</template>

<script setup>
import { computed } from 'vue'
import { merchImageSrc } from '~/utils/merchImage'
import ShopSalePrice from '~/components/Shop/SalePrice.vue'

/** One shop bundle as a card: cover (or a collage of its items), items, price vs. separately. */
const props = defineProps({
  bundle: { type: Object, required: true }
})
const collage = computed(() => (props.bundle.components || []).filter(c => c.image).slice(0, 4))
</script>

<style scoped>
.bundle-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  text-decoration: none;
  transition: transform var(--cy-t-fast, 0.15s) var(--cy-ease, ease), border-color var(--cy-t-fast, 0.15s);
}
.bundle-card:hover { transform: translateY(-2px); border-color: var(--cy-rim-hot, var(--primary)); }
.bundle-card__art { position: relative; aspect-ratio: 1; background: rgba(255, 255, 255, 0.03); }
.bundle-card__art > img { width: 100%; height: 100%; object-fit: cover; }
.bundle-card__collage { display: grid; width: 100%; height: 100%; gap: 2px; }
.bundle-card__collage.n1 { grid-template-columns: 1fr; }
.bundle-card__collage.n2 { grid-template-columns: 1fr 1fr; }
.bundle-card__collage.n3, .bundle-card__collage.n4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
.bundle-card__collage.n3 img:first-child { grid-row: span 2; }
.bundle-card__collage img { width: 100%; height: 100%; object-fit: cover; min-height: 0; }
.bundle-card__badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 2px 8px;
  border-radius: 3px;
  background: var(--primary);
  color: var(--cy-ground, #000);
  font-family: var(--cy-mono);
  font-size: 0.7rem;
  font-weight: 700;
}
.bundle-card__body { padding: 10px 12px 12px; min-width: 0; }
.bundle-card__kicker {
  font-family: var(--cy-mono);
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--primary);
}
.bundle-card__title { color: var(--text-main); font-weight: 700; font-size: 0.9rem; line-height: 1.25; margin-top: 2px; overflow-wrap: anywhere; }
.bundle-card__items { color: var(--text-muted); font-size: 0.72rem; margin-top: 2px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
</style>
